import {
  createContext,
  PropsWithChildren,
  useContext,
  useRef,
  useState,
} from "react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import ErrorModal, { DisplayError } from "../components/Modals/ErrorModal";

export interface UIContextType {
  showError: (message: string) => Promise<boolean>;
}

export const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider = ({ children }: PropsWithChildren) => {
  const [error, setError] = useState<
    string | FetchBaseQueryError | SerializedError | undefined
  >(undefined);

  const resolver = useRef<Function>();

  const showError = (
    error: string | FetchBaseQueryError | SerializedError
  ): Promise<boolean> => {
    setError(error);

    return new Promise(function (resolve) {
      resolver.current = resolve;
    });
  };

  const handleCloseError = () => {
    setError(undefined);
  };

  return (
    <UIContext.Provider value={{ showError }}>
      {children}

      {error && (
        <ErrorModal open={true} onClose={handleCloseError}>
          <DisplayError error={error} />
        </ErrorModal>
      )}
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);

  if (!context) {
    throw new Error("useUI must be used within a UIProvider");
  }

  return context;
};
